import React, { useCallback, useEffect, useRef, useState } from "react";
import { api, API, formatApiErrorDetail } from "@/lib/apiClient";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { UploadSimple, ChatCircle, Trash, DownloadSimple, X } from "@phosphor-icons/react";

const IMAGE_ACCEPT = "image/png,image/jpeg,image/webp,image/gif";

function AttachmentImage({ file, className }) {
  const [src, setSrc] = useState("");
  useEffect(() => {
    let url = "";
    fetch(`${API}/attachments/${file.id}/download`, {
      credentials: "include",
    })
      .then((r) => r.blob())
      .then((b) => { url = URL.createObjectURL(b); setSrc(url); });
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [file.id]);
  return src ? <img src={src} alt={file.original_filename} className={className} /> : <div className={`${className} bg-slate-100`} />;
}

export function PhotoGallery({ parentType, parentId, category = "photo", canEdit = true }) {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [active, setActive] = useState(null);
  const [caption, setCaption] = useState("");
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState("");
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    if (!parentId) return;
    const { data } = await api.get("/attachments", { params: { parent_type: parentType, parent_id: parentId, category } });
    setFiles(data.filter((f) => (f.content_type || "").startsWith("image/")));
  }, [parentType, parentId, category]);
  useEffect(() => { load(); }, [load]);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 20 * 1024 * 1024) return toast.error("File too large (max 20 MB)");
    const fd = new FormData();
    fd.append("file", file);
    setUploading(true);
    try {
      const params = { parent_type: parentType, parent_id: parentId, category };
      if (caption) params.title = caption;
      await api.post("/attachments/upload", fd, { params, headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded");
      setCaption("");
      if (inputRef.current) inputRef.current.value = "";
      await load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally { setUploading(false); }
  };

  const openLightbox = async (f) => {
    setActive(f);
    const { data } = await api.get(`/attachments/${f.id}/comments`);
    setComments(data);
  };

  const addComment = async () => {
    if (!newComment.trim()) return;
    try {
      const { data } = await api.post(`/attachments/${active.id}/comments`, { text: newComment });
      setComments((c) => [...c, data]);
      setNewComment("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const deleteComment = async (cid) => {
    try {
      await api.delete(`/attachments/comments/${cid}`);
      setComments((c) => c.filter((x) => x.id !== cid));
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const deletePhoto = async () => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await api.delete(`/attachments/${active.id}`);
      setActive(null);
      await load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  const downloadPhoto = async () => {
    const res = await fetch(`${API}/attachments/${active.id}/download`, {
      credentials: "include",
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = active.original_filename;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  return (
    <div data-testid={`photo-gallery-${parentType}-${parentId}`}>
      {canEdit && (
        <div className="flex items-end gap-2 mb-3">
          <div className="flex-1">
            <Input
              placeholder="Add a caption (optional)…"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              data-testid="photo-caption-input"
              className="text-sm"
            />
          </div>
          <input ref={inputRef} type="file" accept={IMAGE_ACCEPT} onChange={upload} className="hidden" data-testid="photo-upload-input" />
          <Button onClick={() => inputRef.current?.click()} disabled={uploading} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] h-9" size="sm" data-testid="photo-upload-btn">
            <UploadSimple size={14} className="mr-1" /> {uploading ? "Uploading…" : "Upload photo"}
          </Button>
        </div>
      )}

      {files.length === 0 ? (
        <div className="text-[12px] text-slate-400 py-4 text-center border border-dashed border-slate-200 rounded-sm">No photos yet.</div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2" data-testid="photo-grid">
          {files.map((f) => (
            <div key={f.id} onClick={() => openLightbox(f)} className="relative aspect-square cursor-pointer group bg-slate-100 border border-slate-200 rounded-sm overflow-hidden" data-testid={`photo-${f.id}`}>
              <AttachmentImage file={f} className="w-full h-full object-cover group-hover:opacity-80 transition-opacity" />
              {f.title && (
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-2">
                  <div className="text-white text-[11px] truncate">{f.title}</div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!active} onOpenChange={(o) => { if (!o) { setActive(null); setComments([]); setNewComment(""); } }}>
        <DialogContent className="max-w-5xl max-h-[92vh] p-0 overflow-hidden">
          {active && (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_360px] max-h-[92vh]">
              <div className="bg-black flex items-center justify-center min-h-[400px] max-h-[92vh]">
                <AttachmentImage file={active} className="max-w-full max-h-[92vh] object-contain" />
              </div>
              <div className="bg-white border-l border-slate-200 flex flex-col max-h-[92vh]">
                <div className="p-4 border-b border-slate-100 flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">Photo</div>
                    <div className="font-display font-semibold text-slate-900 truncate">{active.title || active.original_filename}</div>
                    <div className="text-[10px] text-slate-500 mt-0.5">{new Date(active.created_at).toLocaleString("en-IN")}</div>
                  </div>
                  <button onClick={() => setActive(null)} className="text-slate-400 hover:text-slate-800 p-1"><X size={16} /></button>
                </div>

                <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="photo-comments">
                  <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><ChatCircle size={12} /> Comments ({comments.length})</div>
                  {comments.length === 0 ? (
                    <div className="text-[12px] text-slate-400 text-center py-4">No comments yet. Be the first to add one.</div>
                  ) : (
                    comments.map((c) => (
                      <div key={c.id} className="border-b border-slate-100 pb-2 last:border-0" data-testid={`comment-${c.id}`}>
                        <div className="flex items-center justify-between mb-0.5">
                          <div className="text-[12px] font-semibold text-slate-900">{c.author_name}</div>
                          <button onClick={() => deleteComment(c.id)} className="text-slate-400 hover:text-red-600 opacity-0 hover:opacity-100 transition-opacity"><Trash size={11} /></button>
                        </div>
                        <div className="text-[10px] text-slate-500 mb-1">{new Date(c.created_at).toLocaleString("en-IN")}</div>
                        <div className="text-[13px] text-slate-700 whitespace-pre-wrap">{c.text}</div>
                      </div>
                    ))
                  )}
                </div>

                <div className="p-3 border-t border-slate-100 space-y-2">
                  <Textarea
                    rows={2}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Add a comment…"
                    className="text-sm"
                    data-testid="new-comment-input"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={downloadPhoto} className="rounded-sm h-8"><DownloadSimple size={12} className="mr-1" />Download</Button>
                      {canEdit && <Button size="sm" variant="outline" onClick={deletePhoto} className="rounded-sm h-8 text-red-600 hover:text-red-700"><Trash size={12} className="mr-1" />Delete</Button>}
                    </div>
                    <Button size="sm" onClick={addComment} className="rounded-sm bg-[#EA580C] hover:bg-[#c2410c] h-8" data-testid="submit-comment-btn">Post</Button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
